import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { FuseNavigationItem } from '@fuse/components/navigation';
import { IMenu } from '@shared/models/menu';
import { Navigation } from 'app/core/navigation/navigation.types';
import { environment } from 'environments/environment';
import { BehaviorSubject, Observable, firstValueFrom, of } from 'rxjs';
import { compactNavigation, defaultNavigation, futuristicNavigation, horizontalNavigation } from './navigation.data';

@Injectable({ providedIn: 'root' })
export class NavigationService {
    navegacionMenu = [];
    inProcess = false;
    private readonly debugEmbed = true;

    private _menuParsedSubject: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
    private _menuLoadedSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    private _navigation: BehaviorSubject<Navigation> = new BehaviorSubject<Navigation>({
        compact: [],
        default: [],
        futuristic: [],
        horizontal: [],
    });
    private readonly _compactNavigation: FuseNavigationItem[] = compactNavigation;
    private readonly _defaultNavigation: FuseNavigationItem[] = defaultNavigation;
    private readonly _futuristicNavigation: FuseNavigationItem[] = futuristicNavigation;
    private readonly _horizontalNavigation: FuseNavigationItem[] = horizontalNavigation;

    constructor(private _httpClient: HttpClient) {
        this._navigation.next({
            compact: this._compactNavigation,
            default: this.navegacionMenu,
            futuristic: this._futuristicNavigation,
            horizontal: this._horizontalNavigation,
        });
    }

    get navigation$(): Observable<Navigation> {
        return this._navigation.asObservable();
    }

    set _navigationMenuData(menuData) {
        this.navegacionMenu = this.parsearMenuNavegacion(menuData);
        this._navigation.next({
            compact: this._compactNavigation,
            default: this.navegacionMenu,
            futuristic: this._futuristicNavigation,
            horizontal: this._horizontalNavigation,
        });
    }

    get _navigationMenuData() {
        return this.navegacionMenu;
    }

    private matchRoute(itemLink?: string, route?: string): boolean {
        const normalizedItemLink = this.normalizeRoutePath(itemLink);
        const normalizedRoute = this.normalizeRoutePath(route);

        return (
            !!normalizedItemLink &&
            !!normalizedRoute &&
            (normalizedItemLink === normalizedRoute || normalizedRoute.startsWith(`${normalizedItemLink}/`))
        );
    }

    private normalizeRoutePath(value?: string): string {
        if (typeof value !== 'string') {
            return '';
        }

        let normalized = value.trim();
        if (!normalized) {
            return '';
        }

        try {
            normalized = decodeURIComponent(normalized);
        } catch {
            // Keep original value if decode fails.
        }

        if (!normalized.startsWith('/')) {
            normalized = `/${normalized}`;
        }

        const hashIndex = normalized.indexOf('#');
        if (hashIndex >= 0) {
            normalized = normalized.substring(0, hashIndex);
        }

        const queryIndex = normalized.indexOf('?');
        if (queryIndex >= 0) {
            normalized = normalized.substring(0, queryIndex);
        }

        normalized = normalized.replace(/\/+$/, '');
        return normalized || '/';
    }

    private splitRouteAndQuery(route?: string | null): { link: string | null; queryParams: Record<string, string> | null } {
        const rawRoute = typeof route === 'string' ? route.trim() : '';

        if (!rawRoute) {
            return { link: null, queryParams: null };
        }

        let normalizedRoute = rawRoute;
        try {
            normalizedRoute = decodeURIComponent(rawRoute);
        } catch {
            normalizedRoute = rawRoute;
        }

        const [rawPath, ...queryParts] = normalizedRoute.split('?');
        const path = rawPath?.trim() || '/';
        const queryString = queryParts.join('?').trim();

        if (!queryString) {
            return { link: path, queryParams: null };
        }

        const params = new URLSearchParams(queryString);
        const queryParams: Record<string, string> = {};

        params.forEach((value, key) => {
            queryParams[key] = value;
        });

        const result = {
            link: path,
            queryParams: Object.keys(queryParams).length ? queryParams : null,
        };

        return result;
    }

    parsearMenuNavegacion(menu: IMenu[]) {
        const menuData: any[] = [];

        menu.forEach(i => {
            if (i?.item?.orden === -1) {
                return;
            }

            const routeConfig = this.splitRouteAndQuery(i.item.ruta ?? null);
            const { hijos, hiddenChildren } = this.tieneHijos(i.hijos ?? []);

            const item = {
                id: i.item.descripcion.toLowerCase().replace(/\s+/g, ''),
                iditem: i.item.iditem,
                title: i.item.descripcion,
                type: routeConfig.link ? 'basic' : (hijos.length > 0 ? 'collapsable' : 'basic'),
                link: routeConfig.link,
                queryParams: routeConfig.queryParams,
                icon: i.item.icono ?? null,
                children: hijos,
                hiddenChildren,
            };

            menuData.push(item);
        });

        this._menuParsedSubject.next(menuData);
        return menuData;
    }

    tieneHijos(hijos: any[]) {
        const data: any[] = [];
        const hiddenData: any[] = [];

        hijos.forEach(subItem => {
            const { hijos: subHijos, hiddenChildren } = this.tieneHijos(subItem.hijos ?? []);
            const routeConfig = this.splitRouteAndQuery(subItem.item.ruta ?? null);

            const baseItem = {
                id: subItem.item.descripcion.toLowerCase().replace(/\s+/g, ''),
                iditem: subItem.item.iditem,
                title: subItem.item.descripcion,
                type: (subItem.hijos ?? []).length > 0 ? 'collapsable' : 'basic',
                link: routeConfig.link,
                queryParams: routeConfig.queryParams,
                icon: subItem.item.icono ?? null,
                children: subHijos,
            };

            if (subItem.item.orden !== -1) {
                data.push(baseItem);
            } else {
                hiddenData.push({
                    ...baseItem,
                    children: subHijos,
                    hiddenChildren,
                });
            }
        });

        return { hijos: data, hiddenChildren: hiddenData };
    }

    async getMenus() {
        if (!this.inProcess) {
            try {
                const res = await this.getMenusX();
                this.inProcess = false;
                this._menuLoadedSubject.next(res);
            } catch (error) {
                console.error('NavigationService: Error al cargar el menu:', error);
                this.inProcess = false;
                this._menuLoadedSubject.next(false);
            }
        }
    }

    async getMenusX(): Promise<boolean> {
        this.inProcess = true;
        try {
            const res = await firstValueFrom(this._httpClient.post(`${environment.menu}`, null));
            this._navigationMenuData = res['result'];
            return true;
        } catch (error) {
            console.error('NavigationService: Error al cargar getMenusX:', error);
            return false;
        } finally {
            this.inProcess = false;
        }
    }

    get(): Observable<Navigation> {
        const navigationData = {
            compact: this._compactNavigation,
            default: this._navigationMenuData,
            futuristic: this._futuristicNavigation,
            horizontal: this._horizontalNavigation,
        };

        return of(navigationData);
    }

    get menuLoaded$(): Observable<boolean> {
        return this._menuLoadedSubject.asObservable();
    }

    get menuParsed$(): Observable<any[]> {
        return this._menuParsedSubject.asObservable();
    }

    getItemById(id: string): any {
        const items = this._menuParsedSubject.getValue();

        const buscar = (allItems: any[]): any => {
            for (const item of allItems) {
                if (item.id === id) {
                    return item;
                }

                if (item.children?.length > 0) {
                    const encontrado = buscar(item.children);
                    if (encontrado) {
                        return encontrado;
                    }
                }

                if (item.hiddenChildren?.length > 0) {
                    const encontrado = buscar(item.hiddenChildren);
                    if (encontrado) {
                        return encontrado;
                    }
                }
            }

            return null;
        };

        return buscar(items);
    }

    getItemByRoute(route: string): any {
        if (!route) {
            return null;
        }

        const buscar = (items: any[]): any => {
            for (const item of items) {
                if (this.matchRoute(item?.link, route)) {
                    return item;
                }

                if (item.children?.length) {
                    const encontrado = buscar(item.children);
                    if (encontrado) {
                        return encontrado;
                    }
                }

                if (item.hiddenChildren?.length) {
                    const encontrado = buscar(item.hiddenChildren);
                    if (encontrado) {
                        return encontrado;
                    }
                }
            }
            return null;
        };

        return buscar(this.navegacionMenu);
    }

    private shouldDebugRoute(route?: string | null): boolean {
        const candidate = (route || '').toLowerCase();
        return candidate.includes('embed') || candidate.includes('token');
    }
}
